/**
 * AppNotificationBus.jsx — Thin, extensible notification store.
 *
 * NOW:
 *   - push()        → stores up to 100 notifications in memory (newest first)
 *   - unreadCount   → number of unread items (for future bell badge)
 *   - markRead()    → mark one as read
 *   - markAllRead() → mark all as read
 *   - dismiss()     → remove one item
 *   - clearAll()    → wipe all items
 *
 * FUTURE (add without touching call popup or TelephonyContext):
 *   - Render <NotificationBell /> anywhere using useNotificationBus()
 *   - Render <NotificationPanel /> slide-out panel
 *   - Persist to localStorage or IndexedDB
 *   - Hook into WebSocket feeds
 */

import { createContext, useContext, useState, useCallback } from 'react'

const NotificationBusContext = createContext(null)

export function AppNotificationBusProvider({ children }) {
  const [notifications, setNotifications] = useState([]) // newest first, max 100

  const push = useCallback((notif) => {
    const item = {
      id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      read: false,
      // Caller provides: type, title, body, [meta]
      ...notif,
    }
    setNotifications((prev) => [item, ...prev].slice(0, 100))
  }, [])

  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    )
  }, [])

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }, [])

  const dismiss = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const clearAll = useCallback(() => setNotifications([]), [])

  const unreadCount = notifications.filter((n) => !n.read).length

  return (
    <NotificationBusContext.Provider
      value={{
        notifications,
        unreadCount,
        push,
        markRead,
        markAllRead,
        dismiss,
        clearAll,
      }}
    >
      {children}
    </NotificationBusContext.Provider>
  )
}

/**
 * useNotificationBus() — consume the notification bus anywhere in the app.
 *
 * @example
 *   const { push } = useNotificationBus()
 *   push({ type: NOTIFICATION_TYPES.CALL_MISSED, title: 'Missed Call', body: '+91 98765 43210' })
 */
export function useNotificationBus() {
  const ctx = useContext(NotificationBusContext)
  if (!ctx) {
    throw new Error(
      'useNotificationBus must be used inside <AppNotificationBusProvider>. ' +
      'Make sure it wraps your application in App.jsx.'
    )
  }
  return ctx
}
